// frontend/src/pages/CookiePolicy.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguageStore } from '../store/useLanguageStore';
import { Cookie, ArrowLeft, Mail } from 'lucide-react';
import logoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import logoSquare from '../assets/images/datiaFlow_cuadrado.jpg';

/* Anti-scraping email render — bots leen el source, no ejecutan JS */
const SafeEmail = ({ u, d }) => {
  const addr = `${u}\u0040${d}`;
  return <a href={`mai${''}lto:${addr}`} className="text-blue-600 hover:underline">{addr}</a>;
};

const LAST_UPDATED = '2026-08-12';

/* ═══════════════════════════════════════════════════
   TABLA DE COOKIES — datos compartidos entre idiomas
   ═══════════════════════════════════════════════════ */
const COOKIE_TABLE = [
  {
    name: 'vnext_auth_token',
    provider: 'DatiaFlow',
    duration: { ES: 'Sesión (hasta logout)', EN: 'Session (until logout)', PT: 'Sessão (até logout)' },
    type: { ES: 'Estrictamente necesaria', EN: 'Strictly necessary', PT: 'Estritamente necessário' },
    purpose: {
      ES: 'Token JWT de autenticación. Almacenado en localStorage. Necesario para mantener la sesión activa.',
      EN: 'JWT authentication token. Stored in localStorage. Required to keep the session active.',
      PT: 'Token JWT de autenticação. Armazenado em localStorage. Necessário para manter a sessão ativa.'
    }
  },
  {
    name: 'vnext_user',
    provider: 'DatiaFlow',
    duration: { ES: 'Sesión (hasta logout)', EN: 'Session (until logout)', PT: 'Sessão (até logout)' },
    type: { ES: 'Estrictamente necesaria', EN: 'Strictly necessary', PT: 'Estritamente necessário' },
    purpose: {
      ES: 'Datos básicos del usuario autenticado (nombre, rol). Almacenado en localStorage.',
      EN: 'Basic authenticated user data (name, role). Stored in localStorage.',
      PT: 'Dados básicos do usuário autenticado (nome, função). Armazenado em localStorage.'
    }
  },
  {
    name: 'language-storage',
    provider: 'DatiaFlow',
    duration: { ES: 'Persistente', EN: 'Persistent', PT: 'Persistente' },
    type: { ES: 'Estrictamente necesaria', EN: 'Strictly necessary', PT: 'Estritamente necessário' },
    purpose: {
      ES: 'Idioma preferido del usuario (ES/EN/PT). Almacenado en localStorage via Zustand persist.',
      EN: 'User preferred language (ES/EN/PT). Stored in localStorage via Zustand persist.',
      PT: 'Idioma preferido do usuário (ES/EN/PT). Armazenado em localStorage via Zustand persist.'
    }
  },
  {
    name: 'cookie_consent',
    provider: 'DatiaFlow',
    duration: { ES: 'Persistente', EN: 'Persistent', PT: 'Persistente' },
    type: { ES: 'Estrictamente necesaria', EN: 'Strictly necessary', PT: 'Estritamente necessário' },
    purpose: {
      ES: 'Registro de la preferencia de cookies del usuario (analíticas aceptadas/rechazadas). Almacenado en localStorage.',
      EN: 'Record of user cookie preference (analytics accepted/rejected). Stored in localStorage.',
      PT: 'Registro da preferência de cookies do usuário (analíticos aceitos/rejeitados). Armazenado em localStorage.'
    }
  },
  {
    name: '_ga',
    provider: 'Google Analytics',
    duration: { ES: '2 años', EN: '2 years', PT: '2 anos' },
    type: { ES: 'Analítica (requiere consentimiento)', EN: 'Analytics (requires consent)', PT: 'Analítico (requer consentimento)' },
    purpose: {
      ES: 'Identificador único para distinguir usuarios. Genera estadísticas anónimas de uso del sitio.',
      EN: 'Unique identifier to distinguish users. Generates anonymous site usage statistics.',
      PT: 'Identificador único para distinguir usuários. Gera estatísticas anônimas de uso do site.'
    }
  },
  {
    name: '_ga_<ID>',
    provider: 'Google Analytics',
    duration: { ES: '2 años', EN: '2 years', PT: '2 anos' },
    type: { ES: 'Analítica (requiere consentimiento)', EN: 'Analytics (requires consent)', PT: 'Analítico (requer consentimento)' },
    purpose: {
      ES: 'Cookie de persistencia de estado de sesión para Google Analytics 4.',
      EN: 'Session state persistence cookie for Google Analytics 4.',
      PT: 'Cookie de persistência de estado de sessão para Google Analytics 4.'
    }
  },
  {
    name: '_gid',
    provider: 'Google Analytics',
    duration: { ES: '24 horas', EN: '24 hours', PT: '24 horas' },
    type: { ES: 'Analítica (requiere consentimiento)', EN: 'Analytics (requires consent)', PT: 'Analítico (requer consentimento)' },
    purpose: {
      ES: 'Identificador de sesión para agrupar acciones de un mismo usuario durante 24 horas.',
      EN: 'Session identifier to group a user\'s actions within 24 hours.',
      PT: 'Identificador de sessão para agrupar ações de um mesmo usuário durante 24 horas.'
    }
  }
];

/* ═══════════════════════════════════════════════════
   CONTENIDO POR IDIOMA
   ═══════════════════════════════════════════════════ */
const CONTENT = {
  ES: {
    title: 'Política de Cookies',
    lastUpdated: `Última actualización: ${LAST_UPDATED}`,
    backToHome: 'Volver al inicio',
    sections: [
      {
        heading: '1. ¿Qué son las cookies?',
        body: `Las cookies son pequeños archivos de texto que se almacenan en su navegador cuando visita un sitio web. Permiten que el sitio recuerde información sobre su visita, como su idioma preferido y otras configuraciones.

DatiaFlow utiliza principalmente tecnologías de almacenamiento local del navegador (localStorage) en lugar de cookies HTTP tradicionales. El efecto es funcionalmente equivalente: permiten mantener su sesión y preferencias.`
      },
      {
        heading: '2. Tipos de cookies que utilizamos',
        body: `a) Estrictamente necesarias: son imprescindibles para el funcionamiento de la plataforma. Incluyen el token de autenticación (JWT), los datos de sesión del usuario y la preferencia de idioma. No requieren consentimiento porque el servicio no puede funcionar sin ellas.

b) Analíticas: utilizamos Google Analytics 4 (GA4) para comprender cómo los usuarios interactúan con nuestro sitio web. Estas cookies recopilan información de forma anónima y agregada. Requieren su consentimiento previo y no se activan hasta que usted las acepte expresamente.

DatiaFlow NO utiliza cookies de publicidad, marketing ni de seguimiento de terceros para fines publicitarios.`
      },
      {
        heading: '3. Tabla de cookies',
        isTable: true
      },
      {
        heading: '4. Google reCAPTCHA',
        body: `DatiaFlow puede utilizar Google reCAPTCHA v3 en determinados formularios (registro, recuperación de contraseña) para proteger la plataforma contra bots y uso abusivo. reCAPTCHA puede establecer cookies propias de Google para su funcionamiento. Estas cookies son consideradas estrictamente necesarias por su función de seguridad y no requieren consentimiento separado.`
      },
      {
        heading: '5. ¿Cómo gestionar sus cookies?',
        body: `Usted puede gestionar sus preferencias de cookies de las siguientes formas:

a) Banner de cookies: al visitar DatiaFlow por primera vez, se le mostrará un banner con la opción de aceptar o rechazar las cookies analíticas. Puede cambiar su preferencia en cualquier momento volviendo a esta página.

b) Configuración del navegador: puede configurar su navegador para bloquear o eliminar cookies. A continuación, enlaces a las instrucciones de los navegadores más comunes:
— Chrome: chrome://settings/cookies
— Firefox: about:preferences#privacy
— Safari: Preferencias > Privacidad
— Edge: edge://settings/privacy

Tenga en cuenta que bloquear las cookies estrictamente necesarias puede impedir el correcto funcionamiento de la plataforma.`
      },
      {
        heading: '6. Base legal',
        body: `Para titulares sujetos a la Ley 29733, las tecnologías estrictamente necesarias se sustentan en su necesidad para la prestación del servicio solicitado (Art. 14.5); no se invoca el interés legítimo del responsable. Para titulares en la UE, se amparan en el Art. 6.1.b del GDPR.

Las cookies analíticas se basan en el consentimiento del usuario (GDPR Art. 6.1.a / Ley 29733 Art. 13.5 / Directiva ePrivacy Art. 5.3 / LGPD Art. 7.I). No se activan hasta que usted otorgue su consentimiento explícito a través del banner de cookies.`
      },
      {
        heading: '7. Contacto',
        body: `Si tiene preguntas sobre el uso de cookies en DatiaFlow, puede contactarnos en support@varanext.com.`
      }
    ],
    tableHeaders: { name: 'Nombre', provider: 'Proveedor', purpose: 'Finalidad', duration: 'Duración', type: 'Tipo' },
    privacyPolicyLink: 'Política de Privacidad',
    termsLink: 'Términos de Servicio'
  },

  EN: {
    title: 'Cookie Policy',
    lastUpdated: `Last updated: ${LAST_UPDATED}`,
    backToHome: 'Back to home',
    sections: [
      {
        heading: '1. What Are Cookies?',
        body: `Cookies are small text files stored in your browser when you visit a website. They allow the site to remember information about your visit, such as your preferred language and other settings.

DatiaFlow primarily uses browser local storage technologies (localStorage) rather than traditional HTTP cookies. The effect is functionally equivalent: they allow maintaining your session and preferences.`
      },
      {
        heading: '2. Types of Cookies We Use',
        body: `a) Strictly necessary: essential for the platform to function. These include the authentication token (JWT), user session data, and language preference. They do not require consent because the service cannot function without them.

b) Analytics: we use Google Analytics 4 (GA4) to understand how users interact with our website. These cookies collect information anonymously and in aggregate. They require your prior consent and are not activated until you expressly accept them.

DatiaFlow does NOT use advertising, marketing, or third-party tracking cookies for advertising purposes.`
      },
      {
        heading: '3. Cookie Table',
        isTable: true
      },
      {
        heading: '4. Google reCAPTCHA',
        body: `DatiaFlow may use Google reCAPTCHA v3 on certain forms (registration, password recovery) to protect the platform against bots and abusive use. reCAPTCHA may set its own Google cookies for its operation. These cookies are considered strictly necessary due to their security function and do not require separate consent.`
      },
      {
        heading: '5. How to Manage Your Cookies',
        body: `You can manage your cookie preferences in the following ways:

a) Cookie banner: when you first visit DatiaFlow, a banner will be displayed with the option to accept or reject analytics cookies. You can change your preference at any time by returning to this page.

b) Browser settings: you can configure your browser to block or delete cookies. Below are links to instructions for the most common browsers:
— Chrome: chrome://settings/cookies
— Firefox: about:preferences#privacy
— Safari: Preferences > Privacy
— Edge: edge://settings/privacy

Please note that blocking strictly necessary cookies may prevent the platform from functioning correctly.`
      },
      {
        heading: '6. Legal Basis',
        body: `For data subjects under Peru Law 29733, strictly necessary technologies are based on their necessity for providing the requested service (Art. 14.5); the controller's legitimate interest is not invoked. For EU data subjects, they are based on GDPR Art. 6.1.b.

Analytics cookies are based on user consent (GDPR Art. 6.1.a / Peru Law 29733 Art. 13.5 / ePrivacy Directive Art. 5.3 / LGPD Art. 7.I). They are not activated until you grant your explicit consent through the cookie banner.`
      },
      {
        heading: '7. Contact',
        body: `If you have questions about the use of cookies on DatiaFlow, you can contact us at support@varanext.com.`
      }
    ],
    tableHeaders: { name: 'Name', provider: 'Provider', purpose: 'Purpose', duration: 'Duration', type: 'Type' },
    privacyPolicyLink: 'Privacy Policy',
    termsLink: 'Terms of Service'
  },

  PT: {
    title: 'Política de Cookies',
    lastUpdated: `Última atualização: ${LAST_UPDATED}`,
    backToHome: 'Voltar ao início',
    sections: [
      {
        heading: '1. O que são cookies?',
        body: `Cookies são pequenos arquivos de texto armazenados no seu navegador quando você visita um site. Eles permitem que o site lembre informações sobre sua visita, como seu idioma preferido e outras configurações.

O DatiaFlow utiliza principalmente tecnologias de armazenamento local do navegador (localStorage) em vez de cookies HTTP tradicionais. O efeito é funcionalmente equivalente: permitem manter sua sessão e preferências.`
      },
      {
        heading: '2. Tipos de cookies que utilizamos',
        body: `a) Estritamente necessários: são imprescindíveis para o funcionamento da plataforma. Incluem o token de autenticação (JWT), os dados de sessão do usuário e a preferência de idioma. Não requerem consentimento porque o serviço não pode funcionar sem eles.

b) Analíticos: utilizamos o Google Analytics 4 (GA4) para entender como os usuários interagem com nosso site. Esses cookies coletam informações de forma anônima e agregada. Requerem seu consentimento prévio e não são ativados até que você os aceite expressamente.

O DatiaFlow NÃO utiliza cookies de publicidade, marketing nem de rastreamento de terceiros para fins publicitários.`
      },
      {
        heading: '3. Tabela de cookies',
        isTable: true
      },
      {
        heading: '4. Google reCAPTCHA',
        body: `O DatiaFlow pode utilizar o Google reCAPTCHA v3 em determinados formulários (registro, recuperação de senha) para proteger a plataforma contra bots e uso abusivo. O reCAPTCHA pode definir cookies próprios do Google para seu funcionamento. Esses cookies são considerados estritamente necessários por sua função de segurança e não requerem consentimento separado.`
      },
      {
        heading: '5. Como gerenciar seus cookies',
        body: `Você pode gerenciar suas preferências de cookies das seguintes formas:

a) Banner de cookies: ao visitar o DatiaFlow pela primeira vez, será exibido um banner com a opção de aceitar ou rejeitar os cookies analíticos. Você pode alterar sua preferência a qualquer momento retornando a esta página.

b) Configuração do navegador: você pode configurar seu navegador para bloquear ou excluir cookies. Abaixo, links para as instruções dos navegadores mais comuns:
— Chrome: chrome://settings/cookies
— Firefox: about:preferences#privacy
— Safari: Preferências > Privacidade
— Edge: edge://settings/privacy

Observe que bloquear os cookies estritamente necessários pode impedir o correto funcionamento da plataforma.`
      },
      {
        heading: '6. Base legal',
        body: `Para titulares sujeitos à Lei 29733, as tecnologias estritamente necessárias se sustentam em sua necessidade para a prestação do serviço solicitado (Art. 14.5); não se invoca o interesse legítimo do responsável. Para titulares na UE, amparam-se no Art. 6.1.b do GDPR.

Os cookies analíticos baseiam-se no consentimento do usuário (GDPR Art. 6.1.a / Lei 29733 Art. 13.5 / Diretiva ePrivacy Art. 5.3 / LGPD Art. 7.I). Não são ativados até que você conceda seu consentimento explícito através do banner de cookies.`
      },
      {
        heading: '7. Contato',
        body: `Se tiver dúvidas sobre o uso de cookies no DatiaFlow, entre em contato pelo e-mail support@varanext.com.`
      }
    ],
    tableHeaders: { name: 'Nome', provider: 'Provedor', purpose: 'Finalidade', duration: 'Duração', type: 'Tipo' },
    privacyPolicyLink: 'Política de Privacidade',
    termsLink: 'Termos de Serviço'
  }
};

/* ═══════════════════════════════════════════════════
   COOKIE TABLE COMPONENT
   ═══════════════════════════════════════════════════ */
const CookieTable = ({ lang, headers }) => (
  <div className="overflow-x-auto -mx-4 sm:mx-0">
    <table className="w-full text-xs sm:text-sm border border-slate-200 rounded-lg overflow-hidden min-w-[600px]">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          <th className="text-left px-3 py-2.5 font-bold text-slate-600">{headers.name}</th>
          <th className="text-left px-3 py-2.5 font-bold text-slate-600">{headers.provider}</th>
          <th className="text-left px-3 py-2.5 font-bold text-slate-600">{headers.purpose}</th>
          <th className="text-left px-3 py-2.5 font-bold text-slate-600">{headers.duration}</th>
          <th className="text-left px-3 py-2.5 font-bold text-slate-600">{headers.type}</th>
        </tr>
      </thead>
      <tbody>
        {COOKIE_TABLE.map((cookie, i) => (
          <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
            <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{cookie.name}</td>
            <td className="px-3 py-2.5 text-slate-600">{cookie.provider}</td>
            <td className="px-3 py-2.5 text-slate-600">{cookie.purpose[lang]}</td>
            <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{cookie.duration[lang]}</td>
            <td className="px-3 py-2.5">
              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${cookie.type[lang].includes('Analíti') || cookie.type[lang].includes('Analytic') || cookie.type[lang].includes('Analítico')
                ? 'bg-amber-50 text-amber-700'
                : 'bg-green-50 text-green-700'
                }`}>
                {cookie.type[lang]}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
export const CookiePolicy = () => {
  const { language } = useLanguageStore();
  React.useEffect(() => { window.scrollTo(0, 0); }, []);
  const lang = ['ES', 'EN', 'PT'].includes(language?.toUpperCase()) ? language.toUpperCase() : 'ES';
  const c = CONTENT[lang];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex-shrink-0">
            <img src={logoHorizontal} alt="DatiaFlow" className="hidden sm:block h-11 w-auto" />
            <img src={logoSquare} alt="DatiaFlow" className="sm:hidden h-11 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-blue-600 transition-colors flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" />
            {c.backToHome}
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-3 mb-2">
          <Cookie className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl sm:text-3xl font-black text-slate-800">{c.title}</h1>
        </div>
        <p className="text-sm text-slate-400 mb-10">{c.lastUpdated}</p>

        <div className="space-y-8">
          {c.sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-lg font-bold text-slate-800 mb-3">{section.heading}</h2>
              {section.isTable ? (
                <CookieTable lang={lang} headers={c.tableHeaders} />
              ) : (
                <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                  {section.body}
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Footer links */}
        <div className="mt-16 pt-8 border-t border-slate-100 flex flex-wrap gap-6 text-sm">
          <Link to="/privacy" className="text-blue-600 hover:underline">{c.privacyPolicyLink}</Link>
          <Link to="/terms" className="text-blue-600 hover:underline">{c.termsLink}</Link>
          <span className="text-slate-500 hover:text-blue-600 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" />
            <SafeEmail u="support" d="varanext.com" />
          </span>
        </div>
      </main>
    </div>
  );
};
